"use client";

/* eslint-disable react-hooks/exhaustive-deps */

import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useSyncExternalStore } from "react";
import { HelpButton } from "staysure-component-library";

import withBrand from "@/components/with-brand";
import {
  HELP_BUTTON_AVATAR_PATH,
  HELP_OVERLAY_NEW_ICON_PATH,
} from "@/constants";
import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { store } from "@/store/store";

import Styles from "./help-overlay-button.module.css";
import {
  openHelpOverlay,
  closeHelpOverlay,
} from "../../../store/slices/help-overlay.slice";

const HelpOverLay = dynamic(
  () => import("./component/help-overlay/help-overlay"),
  {
    ssr: false,
  }
);

/**
 * This button subscribes to the application store directly, on purpose.
 *
 * `MedicalScreening` creates its own Redux store while it renders, so any
 * re-render of `MedicalApp` (`medical-screen.tsx`) gives the medical library a
 * brand new store. That resets the medical conditions state and unmounts the
 * conditions UI until it is refetched. `MedicalApp` must therefore not
 * subscribe to `helpOverlay.isOpen` — opening or closing help would re-render
 * it and wipe the medical state.
 *
 * `useSelector` cannot be used here either: this button is rendered inside the
 * `MedicalScreening` subtree, which has its own Redux Provider, so a selector
 * would resolve against the medical library's store instead of the
 * application's (where `helpOverlay` does not exist).
 *
 * Reading the application store through `useSyncExternalStore` keeps the
 * overlay state in Redux (it is also opened from outside this component) while
 * leaving `MedicalApp` untouched by help overlay changes. Do not move this
 * subscription back up into `medical-screen.tsx`.
 */
const subscribeToAppStore = (onChange: () => void) => store.subscribe(onChange);
const getHelpOverlayIsOpen = () => store.getState().helpOverlay.isOpen;
const getServerHelpOverlayIsOpen = () => false;

function HelpOverLayButtonMedical(
  props: Readonly<{
    brand?: "staysure" | "avanti";
    useNewHelpOverlay?: boolean;
  }>
) {
  const { brand, useNewHelpOverlay } = props;

  const { dispatch } = store;
  const isOpen = useSyncExternalStore(
    subscribeToAppStore,
    getHelpOverlayIsOpen,
    getServerHelpOverlayIsOpen
  );

  const windowSize = useWindowSize();
  const { t } = useWizardTranslation();
  const ref = React.useRef<HTMLButtonElement>(null);
  const { top, right } = useMemo(() => {
    const buttonRect = ref?.current?.getBoundingClientRect();
    const topVal = buttonRect?.bottom ?? 0;
    const rightVal = buttonRect?.right ?? 0;
    return { top: topVal, right: rightVal };
  }, [ref?.current?.getBoundingClientRect(), windowSize]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        dispatch(closeHelpOverlay());
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch]);

  const getSpacingClass = () => {
    if (!useNewHelpOverlay) return "";
    return isOpen
      ? Styles.help_button_spacing_open
      : Styles.help_button_spacing;
  };

  return (
    <div
      data-testid="help-overlay-button-wrapper-test-id"
      className="w-[165px] h-[58px] bg-surface-neutral-option-2 absolute top-[-1px] right-[-1px] text-text-functional-subtle rounded-bl-[28px]"
    >
      {isOpen && (
        <HelpOverLay
          top={top}
          right={right}
          isOpen={isOpen}
          useNewHelpOverlay={useNewHelpOverlay}
          setIsOpen={(val) => {
            setTimeout(() => {
              val ? dispatch(openHelpOverlay()) : dispatch(closeHelpOverlay());
            }, 100);
          }}
        />
      )}
      <div
        className={`relative z-[101] cursor-pointer mt-[10px] ml-[8px] hover:outline outline-offset-2 outline-interactive-focus-default outline-2 rounded-full text-transparent ${
          Styles.get_help
        } ${getSpacingClass()}`}
      >
        <HelpButton
          id="helpoverlay-automate-test-id"
          tab-index={2}
          isOpen={isOpen}
          buttonRef={ref}
          width={useNewHelpOverlay ? "30px" : "36px"}
          height={useNewHelpOverlay ? "30px" : "36px"}
          onClick={() =>
            dispatch(isOpen ? closeHelpOverlay() : openHelpOverlay())
          }
          avatarSrc={
            useNewHelpOverlay
              ? HELP_OVERLAY_NEW_ICON_PATH
              : HELP_BUTTON_AVATAR_PATH
          }
          brand={brand}
          closeLabel={t("close", "Close")}
          openLabel={t("get_help", "Get Help")}
        />
      </div>
      <div className={` ${Styles.curved_corner_top_right} absolute `} />
      <div className={` ${Styles.curved_corner_bottom_right} absolute `} />
    </div>
  );
}

export default withBrand(HelpOverLayButtonMedical);
